import React from "react";
import { View, TouchableOpacity } from "react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { CloudUpload, Check, Trash2 } from "lucide-react-native";
import { useMedicalHistoryTheme } from "../theme/MedicalHistoryTheme";
import { useTranslations } from "@src/packages/i18n";
import { cardSurface, IconButton } from "@louise/moon-ui";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type UploadedFile = {
  id: string;
  name: string;
  uri: string;
};

export type FileUploadProps = {
  uploadedFiles?: UploadedFile[];
  onUpload: () => void;
  onDelete?: (fileId: string) => void;
  acceptTypes?: string;
  uploading?: boolean;
  error?: string | null;
  onRetry?: () => void;
};

/**
 * File upload component matching the HTML design.
 * Shows uploaded files and an upload button.
 */
export function FileUpload({
  uploadedFiles = [],
  onUpload,
  onDelete,
  uploading = false,
  error = null,
  onRetry,
}: FileUploadProps) {
  const capturePress = usePressCapture("MedicalHistoryFileUpload");
  const theme = useMedicalHistoryTheme();
  const t = useTranslations();

  return (
    <View style={{ width: "100%" }}>
      {/* Uploaded Files */}
      {uploadedFiles.map((file) => (
        <View
          key={file.id}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 16,
            marginBottom: 16,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: "rgba(74, 222, 128, 0.3)",
            backgroundColor: "rgba(74, 222, 128, 0.05)",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              flex: 1,
            }}
          >
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: "rgba(74, 222, 128, 0.2)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Check size={16} color="#4ADE80" strokeWidth={3} />
            </View>
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: "500",
                  color: theme.text,
                  textDecorationLine: "underline",
                  textDecorationColor: `${theme.text}4D`,
                }}
              >
                {file.name}
              </Text>
              <Text
                style={{
                  fontSize: 10.4,
                  fontWeight: "500",
                  color: "#4ADE80",
                  marginTop: 2,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("med_history.components.file_upload.uploaded")}
              </Text>
            </View>
          </View>
          {onDelete && (
            <IconButton
              accessibilityLabel={t("care.beta_result.remove_attached_file", {
                params: { name: file.name },
              })}
              action="remove_uploaded_file"
              onPress={() => onDelete(file.id)}
              size={32}
              pressedOpacity={0.2}
            >
              <Trash2 size={16} color={theme.textFaint} />
            </IconButton>
          )}
        </View>
      ))}

      {uploading ? (
        <View
          style={{
            alignItems: "center",
            borderColor: theme.buttonBorder,
            borderRadius: 12,
            borderWidth: 1,
            flexDirection: "row",
            justifyContent: "center",
            marginBottom: 12,
            padding: 14,
          }}
        >
          <ThinkingOrbStatus
            state="composing"
            label={t("documents.upload.source_uploading")}
            size={20}
          />
        </View>
      ) : null}

      {error ? (
        <View style={{ marginBottom: 12, gap: 6 }}>
          <Text style={{ color: "#DC2626", fontSize: 12 }}>{error}</Text>
          {onRetry ? (
            <TouchableOpacity
              onPress={() => {
                capturePress(
                  pressLabel(t("documents.upload.retry_source")),
                  "documents.upload.retry_source",
                );
                onRetry();
              }}
              style={{ alignSelf: "flex-start" }}
            >
              <Text
                style={{ color: theme.text, fontSize: 12, fontWeight: "700" }}
              >
                {t("documents.upload.retry_source")}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      {/* Upload Button */}
      <TouchableOpacity
        onPress={() => {
          capturePress(
            pressLabel(t("med_history.components.file_upload.upload_cta")),
            "med_history.components.file_upload.upload_cta",
          );
          onUpload();
        }}
        disabled={uploading}
        activeOpacity={0.7}
        style={{
          ...cardSurface({ variant: "prompt" }),
          width: "100%",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: 24,
        }}
      >
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: theme.iconBg,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloudUpload size={20} color={theme.textFaint} />
        </View>
        <View style={{ alignItems: "center" }}>
          <Text
            style={{
              fontSize: 14,
              fontWeight: "500",
              color: theme.text,
              marginBottom: 2,
            }}
          >
            {t("med_history.components.file_upload.upload_cta")}
          </Text>
          <Text style={{ fontSize: 12, color: theme.textFaint }}>
            {t("med_history.components.file_upload.file_types")}
          </Text>
        </View>
      </TouchableOpacity>
    </View>
  );
}
